import { Card, CardContent, CardHeader, Typography, Button, Stack, Box } from '@mui/material';
import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import edit2Fill from '@iconify/icons-eva/edit-2-fill';
import { makeStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import moment from 'moment';
import { LoadingButton } from '@mui/lab';
import { getValue } from 'firebase/remote-config';
import { useSelector } from 'react-redux';
import { Buffer } from 'buffer';
import { useSnackbar } from 'notistack';
import ClockIcon from '../svg/ClockIcon';
import palette from '../theme/palette';
import typography from '../theme/typography';
import BookIcon from '../svg/BookIcon';
import InfoIcon from '../svg/InfoIcon';
import RupeeIcon from '../svg/RupeeIcon';
import { CheckClassIsOnline, GetDurationBetweenTwoDate } from '../helper/commonHelper';
import AddClassPopup from './AddClassPopup';
import ClassTimeTable from './ClassTimeTable';
import { capturePHEvents } from '../analytics/posthog.analytics';
import { PH_JOIN_CLASS } from '../utils/posthog.eventNames';
import { createAttendance } from '../service/attendance.service';
import { remoteConfig } from '../firebase/firebase';
import { getCke } from '../helper/cookieHelper';

const useStyles = makeStyles({
  dotStyle: {
    marginLeft: 10,
    width: 5,
    height: 5,
    borderRadius: '50%',
    backgroundColor: palette.grey[600]
  },
  titleText: {
    textTransform: 'uppercase',
    color: palette.grey[500],
    ...typography.caption,
    marginBottom: 10
  },
  subText: {
    display: 'flex',
    alignItems: 'center'
  },
  infoText: {
    marginLeft: 10,
    textTransform: 'capitalize',
    ...typography.body1
  },
  classInfoWrap: {
    marginTop: 10
  }
});

export default function ClassInfoCard({ isStudent = false, classDetails, handleClassUpdate }) {
  const classes = useStyles();
  const [openEditClass, setEditClass] = useState(false);
  // Time table modal
  const [openTimeTable, setOpenTimeTable] = useState(false);
  // Remote config value
  const isShowClassAllTime = getValue(remoteConfig, 'isShowClassAllTime');
  // User from redux
  const currentUser = useSelector((state) => state.auth.currentUser);
  // Button Loading State
  const [loadingButton, setLoadingButton] = useState(false);
  // Notification
  const { enqueueSnackbar } = useSnackbar();

  const displayCurrentTime = (schedules) => {
    let displayTime = '';
    schedules.forEach((day) => {
      if (day.day === moment().format('dddd') && day.selected) {
        // Get today day with time 12 am in morning
        // For ex: 16/07/2022 12:00 am
        const todayDateWhenDayStart = moment().hour(0).minute(0).second(0).toISOString();
        for (let i = 0; i < day.slots.length; i += 1) {
          // Comparing hour of current time with start and end time.
          const slot = day.slots[i];

          const classBeginningTime = moment(todayDateWhenDayStart)
            .add(moment(slot.from).hour(), 'hour')
            .add(moment(slot.from).minute(), 'minute');
          const classEndTime = moment(todayDateWhenDayStart)
            .add(moment(slot.to).hour(), 'hour')
            .add(moment(slot.to).minute(), 'minute');
          const currentTime = moment();

          if (currentTime.isAfter(classEndTime)) {
            // First we check the current time slot is ended or not.
            // If yes then we going to fetch next slot details
            // eslint-disable-next-line
            continue;
          } else if (
            currentTime.isAfter(classBeginningTime) &&
            currentTime.isBefore(classEndTime)
          ) {
            // Second we check that is current slot is running then we active the green dot and going to fetch next slot details
            displayTime = (
              <>
                <Typography className={classes.infoText}>
                  {moment(slot.from).format('LT').replace('M', '')} -{' '}
                  {moment(slot.to).format('LT').replace('M', '')}
                </Typography>{' '}
                <div className={classes.dotStyle} />{' '}
                <Typography className={classes.infoText}>
                  {GetDurationBetweenTwoDate(classBeginningTime, classEndTime)}
                </Typography>
              </>
            );
            // eslint-disable-next-line
            break;
          } else if (currentTime.isBefore(classBeginningTime)) {
            // Third we check that class is about to start then we display current timing and break the loop here.
            displayTime = (
              <>
                <Typography className={classes.infoText}>
                  {moment(slot.from).format('LT')} - {moment(slot.to).format('LT')}
                </Typography>{' '}
                <div className={classes.dotStyle} />{' '}
                <Typography className={classes.infoText}>
                  {GetDurationBetweenTwoDate(classBeginningTime, classEndTime)}
                </Typography>
              </>
            );
            break;
          }
        }
        if (displayTime === '' && day.slots.length) {
          const slot = day.slots[day.slots.length === 1 ? 0 : day.slots.length - 1];
          displayTime = (
            <>
              <Typography className={classes.infoText}>
                {moment(slot.from).format('LT').replace('M', '')} -{' '}
                {moment(slot.to).format('LT').replace('M', '')}
              </Typography>{' '}
              <div className={classes.dotStyle} />{' '}
              <Typography className={classes.infoText}>
                {GetDurationBetweenTwoDate(
                  moment(todayDateWhenDayStart)
                    .add(moment(slot.from).hour(), 'hour')
                    .add(moment(slot.from).minute(), 'minute'),
                  moment(todayDateWhenDayStart)
                    .add(moment(slot.to).hour(), 'hour')
                    .add(moment(slot.to).minute(), 'minute')
                )}
              </Typography>
            </>
          );
        }
      }
    });
    if (displayTime === '') {
      displayTime = <Typography className={classes.infoText}>No class today</Typography>;
    }
    return displayTime;
  };

  return (
    <div>
      <Card>
        <CardHeader
          action={
            isStudent ? (
              (CheckClassIsOnline(classDetails.schedule) !== null ||
                isShowClassAllTime.asBoolean()) && (
                <LoadingButton
                  variant="contained"
                  sx={{ ml: 1 }}
                  loading={loadingButton}
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setLoadingButton(true);
                    capturePHEvents(PH_JOIN_CLASS, {
                      classDetails,
                      userData: currentUser
                    });
                    const attendanceObj = {
                      orgId: classDetails?.orgId?._id ?? classDetails?.orgId,
                      type: 'online',
                      slotId: CheckClassIsOnline(classDetails.schedule)._id,
                      slot: `slot${
                        classDetails.schedule.findIndex(
                          (ts) => ts._id === CheckClassIsOnline(classDetails.schedule)._id
                        ) + 1
                      }`,
                      slotTimeFrom: CheckClassIsOnline(classDetails.schedule).from,
                      slotTimeTo: CheckClassIsOnline(classDetails.schedule).to,
                      presentStudents: [currentUser?.id],
                      absentStudents: []
                    };
                    const res = await createAttendance(classDetails.id, attendanceObj);
                    if (res) {
                      const obj = {
                        atk: getCke('accessToken'),
                        userName: currentUser?.fullName,
                        userRole: currentUser?.profile,
                        userId: currentUser?.id
                      };
                      const encodedObj = Buffer.from(JSON.stringify(obj)).toString('base64');
                      setLoadingButton(false);
                      window.open(
                        `${process.env.REACT_APP_ZOOM_REDIRECT_URL}/joinClass/${classDetails.id}?fud=${encodedObj}`
                      );
                    } else {
                      enqueueSnackbar('Something went wrong, Please try again', {
                        variant: 'error'
                      });
                      setLoadingButton(false);
                    }
                  }}
                >
                  Join
                </LoadingButton>
              )
            ) : (
              <Button
                variant="text"
                startIcon={<Icon icon={edit2Fill} />}
                style={{ height: 'fit-content' }}
                onClick={() => setEditClass(true)}
              >
                Edit
              </Button>
            )
          }
          title="Class Info"
        />
        <CardContent>
          <div className={classes.classInfoWrap}>
            <Typography className={classes.titleText}>Class time</Typography>
            <div className={classes.subText} style={{ alignItems: 'baseline' }}>
              <ClockIcon />
              <Box>
                <Stack direction="row" alignItems="center">
                  {displayCurrentTime(classDetails?.schedule ?? [])}
                </Stack>
                <Typography
                  sx={{
                    textDecoration: 'underline',
                    color: palette.secondary.main,
                    cursor: 'pointer',
                    ml: '10px',
                    fontSize: '12px'
                  }}
                  onClick={() => setOpenTimeTable(true)}
                >
                  View More
                </Typography>
              </Box>
            </div>
          </div>
          <div className={classes.classInfoWrap}>
            <Typography className={classes.titleText}>Subject</Typography>
            <div className={classes.subText}>
              <BookIcon />
              <Typography className={classes.infoText}>{classDetails.subject}</Typography>
            </div>
          </div>
          {classDetails?.description && (
            <div className={classes.classInfoWrap}>
              <Typography className={classes.titleText}>About Class</Typography>
              <div className={classes.subText}>
                <InfoIcon />
                <Typography className={classes.infoText}>{classDetails?.description}</Typography>
              </div>
            </div>
          )}
          <div className={classes.classInfoWrap}>
            <Typography className={classes.titleText}>Class type</Typography>
            <div className={classes.subText}>
              <RupeeIcon />
              <Typography className={classes.infoText}>
                {classDetails.category} - {classDetails.type}
              </Typography>
            </div>
          </div>
        </CardContent>
      </Card>
      {openEditClass && (
        <AddClassPopup
          isOpen={openEditClass}
          handleClosePopup={() => setEditClass(false)}
          editObj={classDetails}
          handleClassUpdate={handleClassUpdate}
          // handleAddNewClass={(classObj) => handleAddNewClass(classObj)}
        />
      )}

      <ClassTimeTable
        isOpen={openTimeTable}
        handleClose={() => setOpenTimeTable(false)}
        schedule={classDetails?.schedule ?? []}
      />
    </div>
  );
}

ClassInfoCard.propTypes = {
  isStudent: PropTypes.bool,
  handleClassUpdate: PropTypes.func,
  classDetails: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.string,
    category: PropTypes.string,
    orgId: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    subject: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.string,
    schedule: PropTypes.arrayOf(
      PropTypes.shape({
        day: PropTypes.string,
        selected: PropTypes.bool,
        slots: PropTypes.arrayOf(
          PropTypes.shape({
            from: PropTypes.string,
            to: PropTypes.string
          })
        )
      })
    )
  })
};
